import { Injectable, OnDestroy } from '@angular/core';
import { Observable, throwError, ReplaySubject, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { ServiceMessagesService } from '../shared/service-messages.service';
import { DialogConfirmationComponent } from '../shared/components/dialog-confirmation/dialog-confirmation.component';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError, takeUntil, debounceTime, finalize, map } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { DialogAddChannelComponent } from '../account/toolbar/dialog-add-channel/dialog-add-channel.component';

@Injectable({
  providedIn: 'root',
})
export class SharedService implements OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private payments_code_imgs_path = new BehaviorSubject<string>(''); 
  private list: string[] = ['en', 'ru', 'uk'];

  public regex_patterns = {
    email:
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    security_code: /^\d{5}$/,
    password:
      /^((?=.*[A-Z])(?=.*[0-9]))[-A-z0-9!"#$%&\'()*+,.\/:;<=>?@\[\]^_`{|}~]{8,}$/m,
    phone: /^(\+?\d{7,})$/,
  };

  public colors: string[] = [
    '107, 139, 246',  // Светло-синий
    '92, 184, 201',   // Мягкий бирюзовый (между светло-синим и бирюзовым)
    '73, 221, 233',   // Бирюзовый
    '94, 156, 199',   // Приглушённый голубой (между бирюзовым и фиолетовым)
    '124, 91, 246',   // Фиолетовый
    '93, 136, 191',   // Мягкий фиолетовый (между фиолетовым и мягким бирюзовым)
    '63, 182, 184',   // Мягкий бирюзовый
    '192, 205, 128',  // Приглушённый жёлто-зелёный (между мягким бирюзовым и желтым)
    '247, 218, 93',   // Желтый
    '233, 196, 119',  // Приглушённый светло-оранжевый (между желтым и оранжевым)
    '233, 129, 72',   // Оранжевый
    '207, 112, 93',   // Приглушённый кирпичный (между оранжевым и красновато-коричневым)
    '181, 79, 79',    // Красновато-коричневый
    '203, 105, 97',   // Приглушённый теплый красный (между коричневым и насыщенным красным)
    '225, 90, 82',    // Насыщенный красный
    '174, 122, 95',   // Коричневый
    '155, 127, 115',  // Мягкий светло-коричневый (между коричневым и серым)
    '136, 133, 124',  // Серый
    '118, 123, 130',  // Приглушённый серо-синий (между серым и началом палитры)
    '92, 117, 175',   // Мягкий серо-голубой (возвращение к светло-синему)
  ];

  public min_birthday: Date = new Date(1900, 0, 1);
  public max_birthday: Date = new Date(new Date().getFullYear() - 14, new Date().getMonth(), new Date().getDate());

  public theme: BehaviorSubject<string> = new BehaviorSubject('light');
  public month_dict: { title: string; value: string }[];

  constructor(
    private service_messages: ServiceMessagesService,
    private mat_dialog: MatDialog,
    private date_pipe: DatePipe,
    private http: HttpClient
  ) {}

  public get_month_dict(): { title: string; value: string }[] {
    const ls = localStorage.getItem('yoola_app:lang');
    const nav = navigator['language'].split('-')[0];
    const lang = (ls && this.list.includes(ls)) ? ls : (nav && this.list.includes(nav)) ? nav : 'en';

    switch (lang) {
      case 'en':
        return [
          { title: 'January', value: '01' },
          { title: 'February', value: '02' },
          { title: 'March', value: '03' },
          { title: 'April', value: '04' },
          { title: 'May', value: '05' },
          { title: 'June', value: '06' },
          { title: 'July', value: '07' },
          { title: 'August', value: '08' },
          { title: 'September', value: '09' },
          { title: 'October', value: '10' },
          { title: 'November', value: '11' },
          { title: 'December', value: '12' },
        ];

      case 'ru':
        return [
          { title: 'Январь', value: '01' },
          { title: 'Февраль', value: '02' },
          { title: 'Март', value: '03' },
          { title: 'Апрель', value: '04' },
          { title: 'Май', value: '05' },
          { title: 'Июнь', value: '06' },
          { title: 'Июль', value: '07' },
          { title: 'Август', value: '08' },
          { title: 'Сентябрь', value: '09' },
          { title: 'Октябрь', value: '10' },
          { title: 'Ноябрь', value: '11' },
          { title: 'Декабрь', value: '12' },
        ];

      case 'uk':
        return [
          { title: 'Січень', value: '01' },
          { title: 'Лютий', value: '02' },
          { title: 'Березень', value: '03' },
          { title: 'Квітень', value: '04' },
          { title: 'Травень', value: '05' },
          { title: 'Червень', value: '06' },
          { title: 'Липень', value: '07' },
          { title: 'Серпень', value: '08' },
          { title: 'Вересень', value: '09' },
          { title: 'Жовтень', value: '10' },
          { title: 'Листопад', value: '11' },
          { title: 'Грудень', value: '12' },
        ];
    };
  }

  public unic_id() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = crypto.getRandomValues(new Uint8Array(1))[0] % 16;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  
  public update_theme(value = null): void {
    if (value) localStorage.setItem('yoola_app:theme', value);

    const theme: string = localStorage.getItem('yoola_app:theme');
    const system_theme = window.matchMedia('(prefers-color-scheme: dark)');

    this.theme.next(theme === 'system' ? (system_theme.matches ? 'dark' : 'light') : (['light', 'dark'].includes(theme) ? theme : 'light'));

    if (theme === 'system') {
      system_theme.addEventListener('change', () => {
        this.theme.next(system_theme.matches ? 'dark' : 'light');
      });
    } 
  }

  public async update_theme_in_profile_settings(theme: string): Promise<void> {
    this.http.patch<any>(`${environment.API_URL}api/partner/profile/0/`, {theme: theme})
    .pipe(
      takeUntil(this.destroyed$),
      catchError(err => this.service_messages.error(err))
    )
    .subscribe();
  }

  public confirmation(data): Observable<boolean> {
    let dialog_ref: MatDialogRef<DialogConfirmationComponent> =
      this.mat_dialog.open(DialogConfirmationComponent, {
        data: data,
        disableClose: true,
      });      
    return dialog_ref.afterClosed().pipe(takeUntil(this.destroyed$));
  }

  public currency_symbol(value: string, space: boolean = false): string {
    let symbol: string = null;
    switch (value) {
      case 'USD':
        symbol = '$';
        break;
      case 'EUR':
        symbol = '€';
        break;
      case 'RUB':
        symbol = '₽';
        break;
      default:
        symbol = value;
    }
    return !space ? symbol : `${symbol} `;
  }

  public short_number(num, fixed = 2): string {
    if (num === null) {
      return null;
    }
    if (num === 0) {
      return '0';
    }

    let minus = num < 0 ? -1 : 1;
    num = num * minus;

    fixed = !fixed || fixed < 0 ? 0 : fixed;
    let b = num.toPrecision(2).split('e'),
      k = b.length === 1 ? 0 : Math.floor(Math.min(b[1].slice(1), 14) / 3),
      c =
        k < 1
          ? num.toFixed(0 + fixed)
          : (num / Math.pow(10, k * 3)).toFixed(1 + fixed),
      d = c < 0 ? c : Math.abs(c),
      e = d + ['', 'k', 'm', 'b', 't'][k];

    return minus < 0 ? `-${e}` : e;
  }

  public date(v, format): string {
    return this.date_pipe.transform(v, format);
  }

  public color_palette(rgb): string[] {
    const [r, g, b] = rgb.split(',').map(Number);
    const baseColor = { r, g, b };
    const variations = [
      0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1, 0, -0.1, -0.2, -0.3, -0.4,
      -0.5, -0.6, -0.7, -0.8, -0.9, -1,
    ];
    const palette = variations.map((variation) => {
      const color = {
        r: Math.min(255, Math.max(0, Math.round(baseColor.r + variation * 50))),
        g: Math.min(255, Math.max(0, Math.round(baseColor.g + variation * 50))),
        b: Math.min(255, Math.max(0, Math.round(baseColor.b + variation * 50))),
      };

      return `rgb(${color.r}, ${color.g}, ${color.b})`;
    });

    return palette;
  }

  public encode_base64(obj): string {
    let stringToEncode: string;
    if (typeof obj === 'object') {
      stringToEncode = JSON.stringify(obj);
    } else if (typeof obj === 'string') {
      stringToEncode = obj;
    }
    if (stringToEncode.length > 0) {
      return btoa(
        encodeURIComponent(stringToEncode).replace(
          /%([0-9A-F]{2})/g,
          (match, p1) => {
            let code = `0x${p1}`;
            return String.fromCharCode(+code);
          }
        )
      );
    }
  }

  public validate_range(form: FormGroup) {
    let from: Date = form.get('from').value,
      to: Date = form.get('to').value;
    form.get('to').setErrors(null);

    from = from ? new Date(from) : null;
    to = to ? new Date(to) : null;

    if (from && to) {
      if (from.getTime() > to.getTime()) {
        form.get('to').setErrors({ validate_range: { valid: false } });
        return { validate_range: { valid: false } };
      }
    } else {
      if (!from)
        form.get('from').setErrors({ validate_range: { valid: false } });
      if (!to) form.get('to').setErrors({ validate_range: { valid: false } });
    }

    return null;
  }

  public dialog_add_channels() {
    this.mat_dialog.open(DialogAddChannelComponent, {
      disableClose: false,
      width: '460px',
    });
  }

  public get_payment_icon(payment_code?: string): Observable<string> {
    return this.theme
    .pipe(
      map(theme => {
        const base_path = '../../../../../../assets/img/payments/new_payments_icons/';
        const default_icon = `${base_path}transaction_default_${theme}.png`;
  
        if (!payment_code) {
          return default_icon;
        }
  
        const icons: {[key: string]: string } = {
          'crypto_btc': `transaction_crypto_btc_${theme}.png`,
          'crypto_eth': `transaction_crypto_ethereum_${theme}.png`,
          'crypto_usdt_erc20': `transaction_crypto_tether_${theme}.png`,
          'crypto_usdt_trc20': `transaction_crypto_tether_${theme}.png`,
          'yt': `transaction_youtube_${theme}.png`,
          'yt_yoola_stream': `transaction_yt_yoola_stream_${theme}.png`,
          'yt_affiliate_us_tax': `transaction_us_tax_${theme}.png`,
          'yt_affiliate_us_tax_refund': `transaction_us_tax_refund_${theme}.png`,
          'paypal': `transaction_paypal_${theme}.png`,
          'payoneer': `transaction_payoneer_${theme}.png`,
          'privat24': `transaction_privat_${theme}.png`,
          'visa_mastercard': `transaction_bank_${theme}.png`,
          'visa_mastercard_non_rub': `transaction_bank_${theme}.png`,
          'wire_transfer': `transaction_wire_transfer_${theme}.png`,
          'webmoney_usdt': `transaction_webmoney_wmt_${theme}.png`,
          'webmoney_usd': `transaction_webmoney_usd_${theme}.png`,
          'webmoney_usdt_ru': `transaction_webmoney_wmt_${theme}.png`,
          'yoola_music': `transaction_music_${theme}.png`,
          'bank_account_legal_entity_rub': `transaction_wire_transfer_${theme}.png`,
          'bank_account_legal_entity_usd': `transaction_wire_transfer_${theme}.png`,
          'crypto_usdc_erc20': `transaction_usdc_${theme}.png`,
          'crypto_usdc_trc20': `transaction_usdc_${theme}.png`,

          // не проверенные значки 
          'yt_credit_appeal': `transaction_us_tax_refund_${theme}.png`,
          'advance': `transaction_advance_payment_${theme}.png`,
          'bonus': `transaction_bonus_${theme}.png`,
          'yt_us_tax': `transaction_us_tax_${theme}.png`,
          'yt_invalid_traffic': `transaction_invalid_traffic_${theme}.png`,
          'exchange': `transaction_currency_exchange_${theme}.png`,
          'qiwi_wallet': `transaction_qiwi_${theme}.png`,
          'localization_service': `transaction_localization_service_${theme}.png`,
          'yt_direct_sales': `transaction_yoola_brands_${theme}.png`,

          // default
          // cash_transfer
          // balance_transfer
          // yt_ref
          // payment_icon_codes_last_year:
          // fine
          // manual_payment
          // payment_return
          // revenue_adjustment
          // services
          // yt_monetization_disabled
        };
  
        return `${base_path}${icons[payment_code] || `transaction_default_${theme}.png`}`;
      })
    );
  }

  public iso_duration(str): string {
    const match = str.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);
    if (!match) return "00:00:00";
    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseInt(match[3] || '0', 10);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}